import type { RecurringGroupKey, RecurringPayment } from '@/lib/api';
import { dueUtcMs, monthKeyFromParts, monthKeyOf, occurrenceMonthKeyOf } from './occurrence';

/**
 * Where a recurring commitment stands in the current period.
 *
 * `paga` is reserved for account-debited charges (Pix/débito/boleto — money actually left the
 * account). A CARD-backed charge that landed is `na-fatura`: it joined the open invoice and only
 * gets paid with the fatura, so calling it "paga" would lie. `atrasada` is the due date passing
 * with nothing linked. Same vocabulary the home widget uses, so a row reads identically on both
 * surfaces.
 */
export type RecurringPaymentStatusView = 'paga' | 'na-fatura' | 'prevista' | 'atrasada';

export interface RecurringPeriodRef {
  year: number;
  /** 1-based, as the API reports it. */
  month: number;
}

/**
 * The groups already encode the payment rail server-side — `assinaturas` is card-bound by
 * definition — but `fixas` is KIND-based (Utility/Pix), so a utility charged on a credit card
 * also sits on the open fatura. Check the rail too, the same raw-Pluggy inference the
 * transactions screen uses.
 */
export function isCardBacked(item: RecurringPayment, groupKey: RecurringGroupKey): boolean {
  if (groupKey === 'assinaturas' || groupKey === 'parcelamentos') return true;
  return (item.paymentMethod ?? '').toLowerCase().includes('credit');
}

/**
 * Whether the series bills at all in this period. Monthly and weekly always do; a yearly one
 * only in its anniversary month — and a yearly bill sitting out the period must never read as
 * late, so this gates `atrasada`.
 */
function billsThisPeriod(item: RecurringPayment, periodKey: string, landed: boolean): boolean {
  if (item.cadence !== 'Yearly') return true;
  return landed || monthKeyOf(item.nextExpectedDate) === periodKey;
}

/** An ISO date's UTC midnight, or null when it is absent/unparseable. */
function utcDayMs(iso: string | null | undefined): number | null {
  const datePart = iso?.slice(0, 10);
  if (!datePart) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return Date.UTC(year, month - 1, day);
}

function todayUtcMs(now: Date): number {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

function isPastDue(item: RecurringPayment, period: RecurringPeriodRef, now: Date): boolean {
  if (item.cadence === 'Monthly' && item.dayOfMonth && item.dayOfMonth > 0) {
    return dueUtcMs(period.year, period.month - 1, item.dayOfMonth) < todayUtcMs(now);
  }
  const expected = utcDayMs(item.nextExpectedDate);
  return expected != null && expected < todayUtcMs(now);
}

/**
 * A series is settled when the server reports a linked charge for this period — literally the
 * rows that make up `period.pago`. Absent one it falls back to attributing `lastSeen` to an
 * occurrence month: a charge can land without ever reaching `period.pago` (unlinked, or filtered
 * out by the real-expense guards), and reading those as `prevista` would be the worse lie. So the
 * chip is a superset of the aggregate, not a mirror of it.
 */
export function resolveRecurringPaymentStatus(
  item: RecurringPayment,
  groupKey: RecurringGroupKey,
  period: RecurringPeriodRef,
  now: Date = new Date(),
): RecurringPaymentStatusView {
  const periodKey = monthKeyFromParts(period.year, period.month - 1);
  const landed =
    item.settledThisPeriodAt != null ||
    occurrenceMonthKeyOf(item.lastSeen, item.dayOfMonth, item.cadence) === periodKey;

  if (landed) return isCardBacked(item, groupKey) ? 'na-fatura' : 'paga';

  // The detector's own `Missed` flag and a due date that has passed both mean late — but neither
  // can make a series late in a month it does not bill, or a yearly bill flagged Missed once would
  // read "Atrasada" for the other eleven months.
  const late =
    billsThisPeriod(item, periodKey, landed) &&
    (item.status === 'Missed' || isPastDue(item, period, now));
  return late ? 'atrasada' : 'prevista';
}

export interface RecurringStatusDate {
  iso: string;
  /** Whether the date has already happened — the caller words it "venceu" vs "vence". */
  tense: 'past' | 'future';
}

/**
 * The date the row should show for a status: when the charge landed, or when it is owed.
 *
 * Prefers the settlement over `lastSeen` — the detector's own field lags whenever a charge fails a
 * clustering gate. For a late row it prefers the charge that actually failed, because an item the
 * detector flagged `Missed` still carries a FUTURE `nextExpectedDate` (the next occurrence, not the
 * one that failed); the tense follows the date so a row can never read "venceu <future date>".
 */
export function recurringStatusDate(
  item: RecurringPayment,
  status: RecurringPaymentStatusView,
  now: Date = new Date(),
): RecurringStatusDate | null {
  if (status === 'paga' || status === 'na-fatura') {
    const landedAt = item.settledThisPeriodAt ?? item.lastSeen ?? null;
    return landedAt ? { iso: landedAt, tense: 'past' } : null;
  }

  const owedAt =
    (status === 'atrasada' ? item.failedChargeAt : null) ?? item.nextExpectedDate ?? null;
  if (!owedAt) return null;
  const owedMs = utcDayMs(owedAt);
  return { iso: owedAt, tense: owedMs != null && owedMs < todayUtcMs(now) ? 'past' : 'future' };
}
